import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Clock, Calendar, BookOpen, Check, AlertTriangle, Truck, Wallet, Shield, Package, ChevronRight } from 'lucide-react';
import { categories } from '@/mocks/data';

const tocItems = [
  { id: 'what-is', label: 'What Is AllChinaBuy Spreadsheet?' },
  { id: 'how-to-use', label: 'How to Use the Spreadsheet' },
  { id: 'product-research', label: 'Product Research Strategy' },
  { id: 'seller-verification', label: 'Seller Verification & Trust' },
  { id: 'shipping', label: 'Shipping Costs & Methods' },
  { id: 'warehouse', label: 'Warehouse & QC Tips' },
  { id: 'coupons', label: 'Coupons & Discount Codes' },
  { id: 'safety', label: 'Safety & Legitimacy' },
  { id: 'faq', label: 'Frequently Asked Questions' },
];

export default function ArticlePage() {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
        <div className="w-full px-4 md:px-6 lg:px-8 xl:px-12">
          <div className="flex items-center justify-between h-14 md:h-16">
            <a href="/" className="font-heading text-lg font-bold text-foreground-950 tracking-tight">
              {t('site.name')}
            </a>
            <a
              href="/"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('article.backToHome')}
            </a>
          </div>
        </div>
      </nav>

      <article className="flex-1">
        <div className="relative w-full h-64 md:h-80 lg:h-96 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Top-down%20view%20of%20a%20neatly%20organized%20product%20research%20desk%20with%20an%20open%20laptop%20showing%20an%20e-commerce%20spreadsheet%2C%20fashion%20samples%2C%20fabric%20swatches%20and%20a%20coffee%20cup%2C%20bright%20clean%20workspace%2C%20crisp%20natural%20daylight%2C%20modern%20editorial%20photography%2C%20vibrant%20organized%20aesthetic&width=1600&height=900&seq=article-hero-403&orientation=landscape&nocache=true"
            alt="AllChinaBuy Spreadsheet Complete Guide"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 via-foreground-950/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 lg:p-8">
            <div className="max-w-4xl mx-auto">
              <span className="inline-block px-3 py-1 text-xs font-semibold tracking-wider uppercase text-background-50 bg-primary-500/80 backdrop-blur-sm rounded-full mb-3">
                Complete Guide
              </span>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-background-50 leading-tight">
                AllChinaBuy Spreadsheet Complete Guide: How to Find Products, Sellers & Save Money
              </h1>
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 lg:px-8 xl:px-12 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8 pb-6 border-b border-background-200/60">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                August 25, 2026
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                8 min read
              </span>
              <span className="flex items-center gap-1.5">
                <BookOpen className="w-4 h-4" />
                AllChinaBuy Spreadsheet
              </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-8 lg:gap-12">
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-foreground-600 leading-relaxed mb-6">
                  AllChinaBuy Spreadsheet has become one of the most searched resources for international shoppers looking to source products from China. Whether you are searching for shoes, hoodies, bags, or accessories, this comprehensive guide will walk you through everything you need to know about using the AllChinaBuy Spreadsheet effectively.
                </p>

                <div className="bg-primary-50/50 border border-primary-200/50 rounded-xl p-5 md:p-6 mb-8">
                  <h3 className="font-heading text-base font-semibold text-primary-800 mb-3 flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    Quick Answer
                  </h3>
                  <p className="text-foreground-700 text-sm leading-relaxed">
                    AllChinaBuy Spreadsheet is an independent resource hub that organizes product links, seller information, shipping guides, and buying tips to help international shoppers make informed purchasing decisions. Use the spreadsheet to compare products, verify sellers, calculate shipping costs, and find the latest discount codes before placing your order.
                  </p>
                </div>

                <div className="mb-8">
                  <h2 id="what-is" className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600 text-sm font-bold flex-shrink-0">1</span>
                    What Is AllChinaBuy Spreadsheet?
                  </h2>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    AllChinaBuy Spreadsheet is not a traditional e-commerce store. Instead, it functions as a curated database of product listings, seller reviews, and buying resources designed to help shoppers navigate the often complex landscape of international purchasing from China. The spreadsheet format allows users to quickly scan categories, compare prices, and identify trustworthy sellers without spending hours browsing multiple platforms.
                  </p>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    The resource covers over 14 major product categories, including shoes, hoodies, sweaters, t-shirts, jackets, pants, shorts, headwear, accessories, bags, belts, perfumes, and more. Each category is organized to help you find specific products faster, with filtering options that let you sort by price, seller rating, or product type.
                  </p>
                  <p className="text-foreground-600 leading-relaxed">
                    Importantly, AllChinaBuy Spreadsheet is an independent resource. It is not affiliated with any single marketplace or seller, which means the information is designed to be objective and helpful rather than promotional. This independence is exactly why so many shoppers trust it as their starting point for research.
                  </p>
                </div>

                <div className="mb-8">
                  <h2 id="how-to-use" className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600 text-sm font-bold flex-shrink-0">2</span>
                    How to Use the AllChinaBuy Spreadsheet
                  </h2>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    Getting started with AllChinaBuy Spreadsheet is straightforward, but maximizing its value requires understanding a few key features. Here is a step-by-step approach to using the spreadsheet effectively for your shopping needs.
                  </p>

                  <div className="bg-background-100 rounded-xl p-5 md:p-6 mb-4">
                    <h4 className="font-semibold text-foreground-900 mb-3">Step-by-Step Guide</h4>
                    <ol className="space-y-3">
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary-500 text-background-50 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                        <span className="text-foreground-700 text-sm leading-relaxed"><strong>Choose your category</strong> — Start by selecting the product category you are interested in from the main menu or dropdown list.</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary-500 text-background-50 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                        <span className="text-foreground-700 text-sm leading-relaxed"><strong>Filter and sort</strong> — Use the built-in filters to narrow down by price range, seller reputation, or product specifications.</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary-500 text-background-50 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                        <span className="text-foreground-700 text-sm leading-relaxed"><strong>Read seller reviews</strong> — Check the seller rating and recent buyer feedback before clicking through to the product page.</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary-500 text-background-50 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">4</span>
                        <span className="text-foreground-700 text-sm leading-relaxed"><strong>Compare options</strong> — Open multiple product listings in tabs to compare prices, shipping methods, and return policies side by side.</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary-500 text-background-50 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">5</span>
                        <span className="text-foreground-700 text-sm leading-relaxed"><strong>Check for coupons</strong> — Look for active discount codes in the Coupons section before completing your purchase.</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary-500 text-background-50 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">6</span>
                        <span className="text-foreground-700 text-sm leading-relaxed"><strong>Proceed to purchase</strong> — Once you have verified the seller and product, click through to the official buying platform to complete your order.</span>
                      </li>
                    </ol>
                  </div>

                  <p className="text-foreground-600 leading-relaxed">
                    One of the most powerful features of the AllChinaBuy Spreadsheet is the ability to compare products across multiple sellers simultaneously. This comparison capability alone can save you significant money and help you avoid low-quality listings that might otherwise go unnoticed.
                  </p>
                </div>

                <div className="mb-8">
                  <h2 id="product-research" className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600 text-sm font-bold flex-shrink-0">3</span>
                    Product Research Strategy
                  </h2>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    Effective product research is the foundation of successful international shopping. AllChinaBuy Spreadsheet makes this process easier by providing structured data, but you should still apply critical thinking to every purchase decision.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                    <div className="bg-background-100 rounded-xl p-4">
                      <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center mb-2">
                        <Check className="w-4 h-4 text-primary-600" />
                      </div>
                      <h4 className="font-semibold text-foreground-900 text-sm mb-1">Check Photos</h4>
                      <p className="text-foreground-600 text-xs leading-relaxed">Always verify product photos are authentic and not stock images. Look for detailed shots and real-world context.</p>
                    </div>
                    <div className="bg-background-100 rounded-xl p-4">
                      <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center mb-2">
                        <Check className="w-4 h-4 text-primary-600" />
                      </div>
                      <h4 className="font-semibold text-foreground-900 text-sm mb-1">Read Reviews</h4>
                      <p className="text-foreground-600 text-xs leading-relaxed">Look for detailed buyer reviews with photos. Short generic reviews may be less reliable than detailed feedback.</p>
                    </div>
                    <div className="bg-background-100 rounded-xl p-4">
                      <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center mb-2">
                        <Check className="w-4 h-4 text-primary-600" />
                      </div>
                      <h4 className="font-semibold text-foreground-900 text-sm mb-1">Compare Prices</h4>
                      <p className="text-foreground-600 text-xs leading-relaxed">If a price seems too good to be true, it probably is. Use the spreadsheet to establish a price baseline for each category.</p>
                    </div>
                    <div className="bg-background-100 rounded-xl p-4">
                      <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center mb-2">
                        <Check className="w-4 h-4 text-primary-600" />
                      </div>
                      <h4 className="font-semibold text-foreground-900 text-sm mb-1">Check Returns</h4>
                      <p className="text-foreground-600 text-xs leading-relaxed">Verify the seller's return policy before ordering. Some sellers do not accept returns on custom or discounted items.</p>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <h2 id="seller-verification" className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600 text-sm font-bold flex-shrink-0">4</span>
                    Seller Verification & Trust
                  </h2>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    Not all sellers are created equal. The AllChinaBuy Spreadsheet includes seller ratings and historical data to help you identify reliable vendors, but you should also perform your own verification before placing large orders.
                  </p>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    Look for sellers with high transaction volumes and consistent positive feedback over an extended period. New sellers with perfect ratings but few transactions may be riskier than established sellers with slightly lower but more numerous ratings. The spreadsheet's seller history section helps you quickly identify these patterns.
                  </p>
                  <div className="bg-amber-50 border border-amber-200/50 rounded-xl p-4 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-amber-800 text-sm mb-1">Red Flags to Watch For</h4>
                      <ul className="text-amber-700 text-sm space-y-1">
                        <li>• Sellers with no return policy</li>
                        <li>• Prices significantly below market average</li>
                        <li>• Stock photos only with no real product images</li>
                        <li>• Sellers with mostly short, generic reviews</li>
                        <li>• No QC (quality control) photos offered</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <h2 id="shipping" className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600 text-sm font-bold flex-shrink-0">5</span>
                    Shipping Costs & Methods
                  </h2>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    Shipping is often the most confusing part of international shopping. AllChinaBuy Spreadsheet includes shipping cost estimates and method comparisons, but understanding the underlying factors will help you make better decisions.
                  </p>

                  <div className="overflow-x-auto mb-4">
                    <table className="w-full text-sm border border-background-200/60 rounded-xl overflow-hidden">
                      <thead>
                        <tr className="bg-background-100">
                          <th className="px-4 py-3 text-left font-semibold text-foreground-900">Shipping Method</th>
                          <th className="px-4 py-3 text-left font-semibold text-foreground-900">Speed</th>
                          <th className="px-4 py-3 text-left font-semibold text-foreground-900">Best For</th>
                          <th className="px-4 py-3 text-left font-semibold text-foreground-900">Considerations</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-background-200/40">
                        <tr className="bg-background-50">
                          <td className="px-4 py-3 text-foreground-700 font-medium">Standard Shipping</td>
                          <td className="px-4 py-3 text-foreground-600">15-30 days</td>
                          <td className="px-4 py-3 text-foreground-600">Non-urgent orders</td>
                          <td className="px-4 py-3 text-foreground-600">Most affordable option</td>
                        </tr>
                        <tr className="bg-background-50">
                          <td className="px-4 py-3 text-foreground-700 font-medium">Express Shipping</td>
                          <td className="px-4 py-3 text-foreground-600">7-15 days</td>
                          <td className="px-4 py-3 text-foreground-600">Time-sensitive items</td>
                          <td className="px-4 py-3 text-foreground-600">Higher cost, tracking included</td>
                        </tr>
                        <tr className="bg-background-50">
                          <td className="px-4 py-3 text-foreground-700 font-medium">Warehouse Consolidation</td>
                          <td className="px-4 py-3 text-foreground-600">Variable</td>
                          <td className="px-4 py-3 text-foreground-600">Multiple items</td>
                          <td className="px-4 py-3 text-foreground-600">Combine orders to save on shipping</td>
                        </tr>
                        <tr className="bg-background-50">
                          <td className="px-4 py-3 text-foreground-700 font-medium">Air Freight</td>
                          <td className="px-4 py-3 text-foreground-600">5-10 days</td>
                          <td className="px-4 py-3 text-foreground-600">High-value items</td>
                          <td className="px-4 py-3 text-foreground-600">Fastest, premium pricing</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <p className="text-foreground-600 leading-relaxed">
                    Weight is the primary factor in shipping cost calculation. Shoes and jackets are heavier than t-shirts and accessories, so consider consolidating multiple lighter items into one shipment rather than ordering heavy items separately. The AllChinaBuy Spreadsheet shipping calculator helps you estimate these costs before you commit to a purchase.
                  </p>
                </div>

                <div className="mb-8">
                  <h2 id="warehouse" className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600 text-sm font-bold flex-shrink-0">6</span>
                    Warehouse & QC Tips
                  </h2>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    The warehouse stage is where your order gets inspected before international shipping. Understanding how this process works can save you from receiving damaged or incorrect items.
                  </p>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    When your items arrive at the warehouse, they undergo a quality control (QC) inspection. This includes checking for visible defects, verifying size and color, and ensuring the item matches the product description. You should always request QC photos if they are not provided automatically. These photos are your last chance to spot issues before the item ships internationally.
                  </p>
                  <p className="text-foreground-600 leading-relaxed">
                    Warehouse consolidation allows you to combine multiple orders into a single shipment. This is one of the most effective ways to reduce per-item shipping costs. However, consolidation adds a few days to the processing time, so factor this into your delivery expectations.
                  </p>
                </div>

                <div className="mb-8">
                  <h2 id="coupons" className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600 text-sm font-bold flex-shrink-0">7</span>
                    Coupons & Discount Codes
                  </h2>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    Discount codes can significantly reduce your total order cost. AllChinaBuy Spreadsheet maintains an updated list of active coupons, but understanding how to maximize these savings requires a bit of strategy.
                  </p>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    Most coupons have expiration dates and minimum order requirements. Some are category-specific, while others apply sitewide. The spreadsheet notes these restrictions clearly, so you can plan your order to meet the minimum threshold if needed. Seasonal sales events, such as holiday promotions, often offer the deepest discounts, so timing your purchases around these events can yield substantial savings.
                  </p>
                  <p className="text-foreground-600 leading-relaxed">
                    Always verify that a coupon is still active before checkout. Expired codes are a common source of frustration, and the spreadsheet is regularly updated to remove inactive offers. If a code does not work, check the spreadsheet's comments section for user-reported alternatives.
                  </p>
                </div>

                <div className="mb-8">
                  <h2 id="safety" className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600 text-sm font-bold flex-shrink-0">8</span>
                    Safety & Legitimacy
                  </h2>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    Is AllChinaBuy legit? Is it safe? These are the most common questions from new shoppers. The short answer is that the platform itself is a legitimate buying resource, but as with any international marketplace, your safety depends on the individual sellers you choose and the precautions you take.
                  </p>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    AllChinaBuy Spreadsheet does not process payments directly. Instead, it directs you to established buying platforms and sellers. This means your payment security is handled by the platform you actually purchase from, not by AllChinaBuy Spreadsheet itself. Always use secure payment methods that offer buyer protection, such as credit cards or trusted payment processors, and avoid direct bank transfers to unfamiliar sellers.
                  </p>
                  <p className="text-foreground-600 leading-relaxed">
                    The spreadsheet's safety guides cover account security, password management, and how to spot phishing attempts. Following these guidelines is essential for protecting your personal information and financial data while shopping internationally.
                  </p>
                </div>

                <div className="mb-8" id="faq">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600 text-sm font-bold flex-shrink-0">9</span>
                    Frequently Asked Questions
                  </h2>

                  <div className="space-y-3">
                    <details className="group bg-background-100 rounded-xl border border-background-200/60 overflow-hidden">
                      <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                        <span className="font-medium text-foreground-900 text-sm">What is AllChinaBuy Spreadsheet used for?</span>
                        <ChevronRight className="w-4 h-4 text-foreground-500 group-open:rotate-90 transition-transform" />
                      </summary>
                      <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                        AllChinaBuy Spreadsheet is an independent resource hub for organizing product listings, seller information, shipping guides, and buying tips to help international shoppers make informed decisions when purchasing from China.
                      </div>
                    </details>

                    <details className="group bg-background-100 rounded-xl border border-background-200/60 overflow-hidden">
                      <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                        <span className="font-medium text-foreground-900 text-sm">Is AllChinaBuy Spreadsheet free to use?</span>
                        <ChevronRight className="w-4 h-4 text-foreground-500 group-open:rotate-90 transition-transform" />
                      </summary>
                      <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                        Yes, AllChinaBuy Spreadsheet is a free informational resource. It does not charge users for access to product listings, seller data, or buying guides.
                      </div>
                    </details>

                    <details className="group bg-background-100 rounded-xl border border-background-200/60 overflow-hidden">
                      <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                        <span className="font-medium text-foreground-900 text-sm">How do I find the best sellers?</span>
                        <ChevronRight className="w-4 h-4 text-foreground-500 group-open:rotate-90 transition-transform" />
                      </summary>
                      <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                        Use the seller rating filter in the spreadsheet, read recent buyer reviews, look for sellers with high transaction volumes and detailed feedback, and always verify QC photos before approving shipment.
                      </div>
                    </details>

                    <details className="group bg-background-100 rounded-xl border border-background-200/60 overflow-hidden">
                      <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                        <span className="font-medium text-foreground-900 text-sm">What shipping method should I choose?</span>
                        <ChevronRight className="w-4 h-4 text-foreground-500 group-open:rotate-90 transition-transform" />
                      </summary>
                      <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                        Standard shipping is best for non-urgent orders and offers the lowest cost. Express shipping is ideal for time-sensitive items. For multiple items, warehouse consolidation can significantly reduce per-item shipping costs.
                      </div>
                    </details>

                    <details className="group bg-background-100 rounded-xl border border-background-200/60 overflow-hidden">
                      <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                        <span className="font-medium text-foreground-900 text-sm">How can I get discount codes?</span>
                        <ChevronRight className="w-4 h-4 text-foreground-500 group-open:rotate-90 transition-transform" />
                      </summary>
                      <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                        Check the Coupons section of AllChinaBuy Spreadsheet for active discount codes. Pay attention to expiration dates and minimum order requirements. Seasonal sales events typically offer the best discounts.
                      </div>
                    </details>
                  </div>
                </div>

                <div className="bg-primary-50/50 border border-primary-200/50 rounded-xl p-5 md:p-6">
                  <h3 className="font-heading text-base font-semibold text-primary-800 mb-3">Key Takeaways</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-sm text-foreground-700">
                      <Check className="w-4 h-4 text-primary-600 flex-shrink-0 mt-0.5" />
                      AllChinaBuy Spreadsheet is an independent, free resource for international shoppers
                    </li>
                    <li className="flex items-start gap-2 text-sm text-foreground-700">
                      <Check className="w-4 h-4 text-primary-600 flex-shrink-0 mt-0.5" />
                      Always verify seller ratings and request QC photos before shipping
                    </li>
                    <li className="flex items-start gap-2 text-sm text-foreground-700">
                      <Check className="w-4 h-4 text-primary-600 flex-shrink-0 mt-0.5" />
                      Consolidate orders at the warehouse to reduce shipping costs
                    </li>
                    <li className="flex items-start gap-2 text-sm text-foreground-700">
                      <Check className="w-4 h-4 text-primary-600 flex-shrink-0 mt-0.5" />
                      Use active coupons and time purchases around seasonal sales
                    </li>
                    <li className="flex items-start gap-2 text-sm text-foreground-700">
                      <Check className="w-4 h-4 text-primary-600 flex-shrink-0 mt-0.5" />
                      Pay with secure methods that offer buyer protection
                    </li>
                  </ul>
                </div>

                <div className="mt-8 pt-6 border-t border-background-200/60">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">Related Articles</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <a href="/" className="flex items-center gap-3 p-3 rounded-xl bg-background-100 hover:bg-primary-50/50 border border-background-200/60 hover:border-primary-200/50 transition-all">
                      <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                        <Truck className="w-5 h-5 text-primary-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">AllChinaBuy Shipping Cost Guide</p>
                        <p className="text-xs text-foreground-500">Compare shipping methods and costs</p>
                      </div>
                    </a>
                    <a href="/" className="flex items-center gap-3 p-3 rounded-xl bg-background-100 hover:bg-primary-50/50 border border-background-200/60 hover:border-primary-200/50 transition-all">
                      <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                        <Wallet className="w-5 h-5 text-primary-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">AllChinaBuy Coupon Guide</p>
                        <p className="text-xs text-foreground-500">Find active discount codes</p>
                      </div>
                    </a>
                    <a href="/" className="flex items-center gap-3 p-3 rounded-xl bg-background-100 hover:bg-primary-50/50 border border-background-200/60 hover:border-primary-200/50 transition-all">
                      <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                        <Shield className="w-5 h-5 text-primary-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">Is AllChinaBuy Safe?</p>
                        <p className="text-xs text-foreground-500">Safety and legitimacy guide</p>
                      </div>
                    </a>
                    <a href="/" className="flex items-center gap-3 p-3 rounded-xl bg-background-100 hover:bg-primary-50/50 border border-background-200/60 hover:border-primary-200/50 transition-all">
                      <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                        <Package className="w-5 h-5 text-primary-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">Warehouse & QC Guide</p>
                        <p className="text-xs text-foreground-500">Quality control and storage tips</p>
                      </div>
                    </a>
                  </div>
                </div>

                <div className="mt-8 p-5 md:p-6 rounded-2xl bg-foreground-950 text-background-100 text-center">
                  <h3 className="font-heading text-lg md:text-xl font-bold text-background-50 mb-2">Ready to Start Shopping?</h3>
                  <p className="text-sm text-background-300 mb-4">Explore the full AllChinaBuy Spreadsheet and find your next purchase.</p>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-xl transition-colors"
                  >
                    Explore AllChinaBuy Spreadsheet
                  </a>
                </div>

                <div className="mt-6 text-xs text-foreground-500 bg-amber-50/50 border border-amber-200/50 rounded-xl p-4">
                  <p className="leading-relaxed">
                    <strong>Disclaimer:</strong> AllChinaBuySpread is an independent informational resource and is not the official AllChinaBuy website. Information is provided for educational purposes and may change. Always verify important details directly with sellers before making purchasing decisions. We do not process payments or guarantee product quality.
                  </p>
                </div>
              </div>

              <aside className="hidden lg:block">
                <div className="sticky top-24">
                  <div className="bg-background-100 rounded-xl border border-background-200/60 p-5 mb-6">
                    <h3 className="font-semibold text-foreground-900 text-sm mb-4 flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-primary-600" />
                      Table of Contents
                    </h3>
                    <ul className="space-y-1.5">
                      {tocItems.map((item) => (
                        <li key={item.id}>
                          <button
                            onClick={() => scrollToSection(item.id)}
                            className="text-left text-sm text-foreground-600 hover:text-primary-600 transition-colors w-full py-1"
                          >
                            {item.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-background-100 rounded-xl border border-background-200/60 p-5 mb-6">
                    <h3 className="font-semibold text-foreground-900 text-sm mb-4">Product Categories</h3>
                    <ul className="space-y-2">
                      {categories.slice(0, 8).map((cat) => (
                        <li key={cat.name}>
                          <a
                            href={cat.url}
                            target="_blank"
                            rel="noopener nofollow"
                            className="text-xs text-foreground-600 hover:text-primary-600 transition-colors inline-flex items-center gap-1"
                          >
                            {cat.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-primary-50/50 rounded-xl border border-primary-200/50 p-5">
                    <h3 className="font-semibold text-primary-800 text-sm mb-2">AllChinaBuy Spreadsheet</h3>
                    <p className="text-xs text-primary-700 leading-relaxed mb-3">
                      Your independent resource for product research, seller reviews, and buying guides.
                    </p>
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:text-primary-700"
                    >
                      Visit Main Site
                      <ChevronRight className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}